// THE THINKING PHASE, SHOWN: while a conversation turn blocks, this band replaces the bare
// "thinking…" with what is actually happening — the clock, the token count, the tool calls,
// the last words streamed. Data comes from GET /api/agent/runs/:id/activity, polled by the
// host panel; this component only renders and keeps two things honest on its own:
//   · the clock ticks locally off the turn's anchor (or the host's send timestamp when the
//     run id is not known yet — the first turn of a fresh thread);
//   · the token counter INTERPOLATES between the server's step-milestone values at the
//     model's cruise rate, pauses while a tool runs (the model is not generating), snaps up
//     to every real value, and wears "≈" whenever it is ahead of one.
import { useEffect, useRef, useState } from 'react';

import type { TurnActivity } from '../api/agent.js';
import { useT } from '../i18n/index.js';
import './ChatActivity.de.js';
import './ChatActivity.fr.js';

/** qwen3 on the halcyon 4×3090 generates at roughly this rate — display cruise, not truth. */
const TOKENS_PER_SECOND = 22;
/** Never run further ahead of the last real value than this before waiting for the server. */
const MAX_LEAD = 350;

export function ChatActivity({ activity, startedAt }: {
  activity: TurnActivity | null;
  /** The host's own send timestamp — the clock's fallback anchor before the run id is known. */
  startedAt: number;
}) {
  const t = useT();
  const [, tick] = useState(0);
  const shownRef = useRef(0);
  const serverRef = useRef(0);
  useEffect(() => {
    const iv = setInterval(() => {
      const toolActive = (activity?.tools ?? []).some((o) => o.status === 'running' || o.status === 'pending');
      if (!toolActive && shownRef.current > 0 && shownRef.current < serverRef.current + MAX_LEAD) {
        shownRef.current += TOKENS_PER_SECOND;
      }
      tick((n) => n + 1);
    }, 1000);
    return () => clearInterval(iv);
  }, [activity]);

  const raw = activity?.tokens.output ?? 0;
  if (raw > serverRef.current) serverRef.current = raw;
  if (serverRef.current > shownRef.current) shownRef.current = serverRef.current;
  const shown = Math.round(shownRef.current);
  const estimated = shownRef.current > serverRef.current || (activity?.tokens.estimated ?? false);

  const since = activity?.since ?? startedAt;
  const elapsed = Math.max(0, Math.floor((Date.now() - since) / 1000));
  const clock = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  const tools = activity?.tools ?? [];
  const visible = tools.slice(-4);

  return (
    <div className="chat-act">
      <div className="chat-act-head">
        <span className="chat-act-dots" aria-hidden="true"><span /><span /><span /></span>
        <span>{t('Answering…')}</span>
        <span className="chat-act-meta voice-figure">
          ⏱ {clock}
          {shown > 0 ? <> · {estimated ? '≈ ' : ''}{shown.toLocaleString()} {t('tokens')}</> : null}
          {tools.length > 4 ? <> · {tools.length} {t('tool calls')}</> : null}
        </span>
      </div>
      {visible.map((o, i) => (
        <div
          key={`${i}-${o.tool}-${o.detail}`}
          className={`chat-act-tool${o.status === 'running' || o.status === 'pending' ? ' live' : ''}`}
        >
          <span className="chat-act-dot" aria-hidden="true" />
          <strong>{o.tool}</strong>
          {o.detail ? <span className="chat-act-detail">{o.detail}</span> : null}
        </div>
      ))}
      {activity?.preview ? <div className="chat-act-preview">{activity.preview}</div> : null}
    </div>
  );
}

export default ChatActivity;
